// API Service Layer for AWS Monitor v2
import axios, { AxiosInstance } from 'axios';

// Use relative URLs since we're behind the same domain
const API_BASE_URL = '';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies/session
  // Serialize array params without brackets (e.g. tag_values=A&tag_values=B)
  // so FastAPI Query(List[str]) can parse them correctly.
  paramsSerializer: {
    serialize: (params) => {
      const sp = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          value.forEach((v) => sp.append(key, String(v)));
        } else if (value !== null && value !== undefined) {
          sp.append(key, String(value));
        }
      }
      return sp.toString();
    },
  },
});

// Handle response errors globally (cookies are sent automatically)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Service endpoints for each microservice
export const services = {
  // Auth Service
  auth: {
    login: (credentials) => api.post('/api/v2/auth/local/login', credentials),
    logout: () => api.post('/api/v2/auth/local/logout'),
    getCurrentUser: () => api.get('/api/v2/auth/users/me'),
    refreshToken: () => api.post('/api/v2/auth/tokens/refresh'),
    googleLogin: (token) => api.post('/api/v2/auth/oauth/google/verify', { token }),
    googleLogout: () => api.post('/api/v2/auth/oauth/logout'),
    listUsers: (params) => api.get('/api/v2/auth/users', { params }),
    // Profile management
    updateMyProfile: (payload) => api.put('/api/v2/auth/users/me', payload),
    updateUserProfile: (userId, payload) => api.put(`/api/v2/auth/users/${userId}/profile`, payload),
    updateUserRoles: (userId, roles) => api.put(`/api/v2/auth/users/${userId}/roles`, { roles }),
    updateUserStatus: (userId, isActive) =>
      api.put(`/api/v2/auth/users/${userId}/status`, { is_active: isActive }),
    listRoles: () => api.get('/api/v2/auth/roles'),
    createRole: (payload) => api.post('/api/v2/auth/roles', payload),
    updateRole: (roleId, payload) => api.put(`/api/v2/auth/roles/${roleId}`, payload),
    deleteRole: (roleId) => api.delete(`/api/v2/auth/roles/${roleId}`),
    listPermissions: () => api.get('/api/v2/auth/roles/permissions'),
    // User Groups
    listGroups: () => api.get('/api/v2/auth/groups'),
    createGroup: (payload) => api.post('/api/v2/auth/groups', payload),
    getGroup: (groupId) => api.get(`/api/v2/auth/groups/${groupId}`),
    updateGroup: (groupId, payload) => api.put(`/api/v2/auth/groups/${groupId}`, payload),
    deleteGroup: (groupId) => api.delete(`/api/v2/auth/groups/${groupId}`),
    addGroupMember: (groupId, payload) => api.post(`/api/v2/auth/groups/${groupId}/members`, payload),
    removeGroupMember: (groupId, userId) => api.delete(`/api/v2/auth/groups/${groupId}/members/${userId}`),
    assignGroupResource: (groupId, payload) => api.post(`/api/v2/auth/groups/${groupId}/resources`, payload),
    unassignGroupResource: (groupId, resourceType, resourceId) =>
      api.delete(`/api/v2/auth/groups/${groupId}/resources/${resourceType}/${resourceId}`),
    // Self-service group management
    getMyGroupMemberships: () => api.get('/api/v2/auth/groups/my-memberships'),
    selfAssignGroups: (groupNames: string[]) => api.post('/api/v2/auth/groups/self-assign', { group_names: groupNames }),
    // Notification Preferences
    getNotificationPreferences: () => api.get('/api/v2/auth/notifications/preferences'),
    updateNotificationPreferences: (payload) => api.put('/api/v2/auth/notifications/preferences', payload),
    sendTestNotification: (email?) => api.post('/api/v2/auth/notifications/test', { email }),
    getNotificationHealth: () => api.get('/api/v2/auth/notifications/health'),
  },

  // Incidents
  incidents: {
    list: (params) => api.get('/api/v2/incidents', { params }),
    getActive: (resourceType?) => api.get('/api/v2/incidents/active', { params: { resource_type: resourceType } }),
    getStats: () => api.get('/api/v2/incidents/stats'),
    get: (id) => api.get(`/api/v2/incidents/${id}`),
    acknowledge: (id) => api.post(`/api/v2/incidents/${id}/acknowledge`),
    resolve: (id) => api.post(`/api/v2/incidents/${id}/resolve`),
  },

  // EC2 Service
  ec2: {
    getInstances: (params) => api.get('/api/v2/ec2/instances', { params }),
    // Default to NOT include metrics to avoid duplicate heavy calls; users are fetched separately.
    getInstance: (id, opts: { includeMetrics?: boolean; includeUsers?: boolean } = { includeMetrics: false, includeUsers: false }) => {
      const include_metrics = opts.includeMetrics === true;
      const include_users = !!opts.includeUsers; // false by default
      const qp = new URLSearchParams();
      if (include_metrics) qp.set('include_metrics', 'true');
      if (include_users) qp.set('include_users', 'true');
      const qs = qp.toString();
      const url = `/api/v2/ec2/instances/${id}` + (qs ? `?${qs}` : '');
      return api.get(url);
    },
    getInstanceMetrics: (id, params) => api.get(`/api/v2/ec2/instances/${id}/metrics`, { params }),
    getInstanceProcesses: (id) => api.get(`/api/v2/ec2/instances/${id}/processes`),
    getInstanceUsers: (id) => api.get(`/api/v2/ec2/instances/${id}/users`),
    executeCommand: (id, command) => api.post(`/api/v2/ec2/instances/${id}/execute`, { command }),
    // The route is /metrics/top-processes/{instance_id}; sort is "cpu" or "memory".
    getTopProcesses: (instanceId: string, params?: { limit?: number; sort?: 'cpu' | 'memory' }) =>
      api.get(`/api/v2/ec2/metrics/top-processes/${instanceId}`, { params }),
    getMetricsGraph: (instanceId, params) => api.get(`/api/v2/ec2/metrics/graph/${instanceId}`, { params }),
    startInstance: (id: string) => api.post(`/api/v2/ec2/instances/${id}/start`),
    stopInstance: (id: string) => api.post(`/api/v2/ec2/instances/${id}/stop`),
    rebootInstance: (id: string) => api.post(`/api/v2/ec2/instances/${id}/reboot`),
    getProductTags: () => api.get('/api/v2/ec2/instances/tags/product'),
  },

  // RDS Service
  rds: {
    getInstances: (params) => api.get('/api/v2/rds/instances', { params }),
    getInstance: (id) => api.get(`/api/v2/rds/instances/${id}`),
    getInstanceMetrics: (id, params) => api.get(`/api/v2/rds/instances/${id}/metrics`, { params }),
    // Correct endpoint under /metrics for Performance Insights
    getPerformanceInsights: (id, params) => api.get(`/api/v2/rds/metrics/performance-insights/${id}`, { params }),
    getSnapshots: (id) => api.get(`/api/v2/rds/instances/${id}/snapshots`),
    getEvents: (id) => api.get(`/api/v2/rds/instances/${id}/events`),
    // Graph endpoint requires instance id in path
    getMetricsGraph: (id, params) => api.get(`/api/v2/rds/metrics/graph/${id}`, { params }),
    startInstance: (id: string) => api.post(`/api/v2/rds/instances/${id}/start`),
    stopInstance: (id: string) => api.post(`/api/v2/rds/instances/${id}/stop`),
    getProductTags: () => api.get('/api/v2/rds/instances/tags/product'),
  },

  // Threshold Alerting (generic engine, served by the RDS service)
  alerts: {
    getCatalog: () => api.get('/api/v2/rds/alerts/catalog'),
    getAccounts: () => api.get('/api/v2/rds/alerts/accounts'),
    getResources: (resourceType: string, region?: string) =>
      api.get('/api/v2/rds/alerts/resources', {
        params: { resource_type: resourceType, ...(region ? { region } : {}) },
      }),
    getRules: (params?: Record<string, unknown>) =>
      api.get('/api/v2/rds/alerts/rules', { params }),
    getRule: (id: string) => api.get(`/api/v2/rds/alerts/rules/${id}`),
    createRule: (payload: unknown) =>
      api.post('/api/v2/rds/alerts/rules', payload),
    updateRule: (id: string, payload: unknown) =>
      api.put(`/api/v2/rds/alerts/rules/${id}`, payload),
    deleteRule: (id: string) => api.delete(`/api/v2/rds/alerts/rules/${id}`),
    getRuleEvents: (id: string, params?: Record<string, unknown>) =>
      api.get(`/api/v2/rds/alerts/rules/${id}/events`, { params }),
    evaluateRule: (id: string) => api.post(`/api/v2/rds/alerts/rules/${id}/evaluate`),
    acknowledgeRule: (id: string) => api.post(`/api/v2/rds/alerts/rules/${id}/acknowledge`),
    sendTestAlert: (id: string, payload?: unknown) =>
      api.post(`/api/v2/rds/alerts/rules/${id}/test`, payload ?? null),
  },

  // DIMS database alerting (DIMS > DB Alerts); served by the rds service.
  dimsAlerts: {
    getOverview: () => api.get('/api/v2/rds/alerts/dims/overview'),
    getEvents: (params?: Record<string, unknown>) =>
      api.get('/api/v2/rds/alerts/dims/events', { params }),
    getCatalog: () => api.get('/api/v2/rds/alerts/dims/catalog'),
    getEntities: (refresh = false) =>
      api.get('/api/v2/rds/alerts/dims/entities', { params: refresh ? { refresh: true } : {} }),
    preview: (payload: unknown) => api.post('/api/v2/rds/alerts/dims/preview', payload),
    createRule: (payload: unknown) => api.post('/api/v2/rds/alerts/dims/rules', payload),
    updateRule: (id: string, payload: unknown) =>
      api.put(`/api/v2/rds/alerts/dims/rules/${id}`, payload),
    deleteRule: (id: string) => api.delete(`/api/v2/rds/alerts/dims/rules/${id}`),
    evaluateRule: (id: string) => api.post(`/api/v2/rds/alerts/dims/rules/${id}/evaluate`),
    acknowledgeRule: (id: string) => api.post(`/api/v2/rds/alerts/dims/rules/${id}/acknowledge`),
    testRule: (id: string, payload?: unknown) =>
      api.post(`/api/v2/rds/alerts/dims/rules/${id}/test`, payload ?? null),
    getRuleNotifications: (id: string) =>
      api.get(`/api/v2/rds/alerts/dims/rules/${id}/notifications`),
    getMySubscriptions: () => api.get('/api/v2/rds/alerts/dims/subscriptions/me'),
    setMySubscription: (categoryId: string, subscribed: boolean) =>
      api.put('/api/v2/rds/alerts/dims/subscriptions/me', { category_id: categoryId, subscribed }),
    getSubscriptions: (params?: Record<string, unknown>) =>
      api.get('/api/v2/rds/alerts/dims/subscriptions', { params }),
    addSubscriptions: (payload: unknown) => api.post('/api/v2/rds/alerts/dims/subscriptions', payload),
    unsubscribe: (id: string) => api.post(`/api/v2/rds/alerts/dims/subscriptions/${id}/unsubscribe`),
    resubscribe: (id: string) => api.post(`/api/v2/rds/alerts/dims/subscriptions/${id}/resubscribe`),
    getCategories: () => api.get('/api/v2/rds/alerts/dims/categories'),
    createCategory: (payload: unknown) => api.post('/api/v2/rds/alerts/dims/categories', payload),
    updateCategory: (id: string, payload: unknown) =>
      api.put(`/api/v2/rds/alerts/dims/categories/${id}`, payload),
    deleteCategory: (id: string) => api.delete(`/api/v2/rds/alerts/dims/categories/${id}`),
    getChannels: () => api.get('/api/v2/rds/alerts/dims/channels'),
    updateChannel: (id: string, payload: unknown) =>
      api.put(`/api/v2/rds/alerts/dims/channels/${id}`, payload),
    testChannel: (id: string) => api.post(`/api/v2/rds/alerts/dims/channels/${id}/test`),
    getRetention: () => api.get('/api/v2/rds/alerts/dims/retention'),
    runRetention: (dryRun: boolean) =>
      api.post('/api/v2/rds/alerts/dims/retention/run', { dry_run: dryRun }),
    getAccess: () => api.get('/api/v2/rds/alerts/dims/access'),
    grantAccess: (email: string, level: string, note?: string) =>
      api.post('/api/v2/rds/alerts/dims/access', { email, level, note }),
    revokeAccess: (email: string) => api.post('/api/v2/rds/alerts/dims/access/revoke', { email }),
  },

  // S3 Service
  s3: {
    getBuckets: (params) => api.get('/api/v2/s3/buckets', { params }),
    getBucket: (name) => api.get(`/api/v2/s3/buckets/${name}`),
    getBucketMetrics: (name) => api.get(`/api/v2/s3/buckets/${name}/metrics`),
    getBucketQuota: (name) => api.get(`/api/v2/s3/buckets/${name}/quota`),
    updateBucketQuota: (name, quota) => api.put(`/api/v2/s3/buckets/${name}/quota`, quota),
    getUtilization: (name) => api.get(`/api/v2/s3/buckets/${name}/utilization`),
    getComplianceStatus: (name) => api.get(`/api/v2/s3/buckets/${name}/compliance`),
    // New: S3 CloudWatch-backed timeseries for request/latency metrics
    getMetricsGraph: (name, params) => api.get(`/api/v2/s3/metrics/graph/${name}`, { params }),
  },

  // URLs Monitoring Service
  urls: {
    getMonitors: (params) => api.get('/api/v2/urls', { params }),
    getMonitor: (id) => api.get(`/api/v2/urls/${id}`),
    createMonitor: (data) => api.post('/api/v2/urls', data),
    updateMonitor: (id, data) => api.put(`/api/v2/urls/${id}`, data),
    deleteMonitor: (id) => api.delete(`/api/v2/urls/${id}`),
    checkUrl: (id) => api.post(`/api/v2/urls/${id}/check`),
    checkUrlAndNotify: (id) => api.post(`/api/v2/urls/${id}/check-and-notify`),
    sendStatusNotification: (id) => api.post(`/api/v2/urls/${id}/send-status-notification`),
    getMetrics: (id) => api.get(`/api/v2/urls/${id}/metrics`),
    getCheckHistory: (id, params) => api.get(`/api/v2/urls/${id}/history`, { params }),
    checkBulk: (urls) => api.post('/api/v2/urls/check-bulk', { urls }),
    // New: Aggregated timeseries from URL history
    getMetricsGraph: (id, params) => api.get(`/api/v2/urls/metrics/graph/${id}`, { params }),
    // New: Get monitoring status with recent status changes
    getMonitoringStatus: () => api.get('/api/v2/urls/monitoring/status'),
  },

  // Metrics Aggregation Service
  metrics: {
    getAggregated: (params) => api.get('/api/v2/metrics/metrics/aggregate', { params }),
    getServiceMetrics: (service, params) => api.get(`/api/v2/metrics/metrics/services/${service}`, { params }),
    compareMetrics: (params) => api.get('/api/v2/metrics/metrics/comparison', { params }),
    getOverviewDashboard: (params) => api.get('/api/v2/metrics/dashboards/overview', { params }),
    getOverviewTimeseries: (params) => api.get('/api/v2/metrics/dashboards/overview-timeseries', { params }),
    getServiceDashboard: (service, params) => api.get(`/api/v2/metrics/dashboards/service/${service}`, { params }),
    // cost summary removed
  },

  // Billing Service (AWS Cost Explorer)
  billing: {
    getCosts: (params) => api.get('/api/v2/billing/costs', { params }),
  },

  // DIMS LIS Computers Service
  dims: {
    getComputers: (params?) => api.get('/api/v2/dims/computers', { params }),
    getDbMonitor: () => api.get('/api/v2/dims/db-monitor'),
  },

  // Bot Scan Service
  botscan: {
    getTargets: (refresh = false) =>
      api.get('/api/v2/botscan/scan/targets', { params: refresh ? { refresh: true } : {} }),
    // Returns immediately with a job id; the scan continues server-side.
    createJob: (payload: { target_type: string; target: string; minutes: number }) =>
      api.post('/api/v2/botscan/scan/jobs', payload),
    getJob: (jobId: string) => api.get(`/api/v2/botscan/scan/jobs/${jobId}`),
    listJobs: (params?: { limit?: number; offset?: number; mine?: boolean }) =>
      api.get('/api/v2/botscan/scan/jobs', { params }),
    downloadReport: (jobId: string) =>
      api.get(`/api/v2/botscan/scan/jobs/${jobId}/report.pdf`, { responseType: 'blob' }),
    getBlockStatus: (loadBalancerArn: string) =>
      api.get('/api/v2/botscan/scan/block/status', { params: { load_balancer_arn: loadBalancerArn } }),
    blockIps: (payload: {
      load_balancer_arn: string;
      load_balancer_name: string;
      ips: string[];
      create_web_acl?: boolean;
    }) => api.post('/api/v2/botscan/scan/block', payload),
    unblockIps: (payload: {
      load_balancer_arn: string;
      load_balancer_name: string;
      ips: string[];
    }) => api.post('/api/v2/botscan/scan/unblock', payload),
    deleteJobs: (ids: string[]) =>
      api.post('/api/v2/botscan/scan/jobs/delete', { ids }),
    getHistoryStats: () => api.get('/api/v2/botscan/scan/jobs/stats'),
  },

  // Gateway Health & Status
  gateway: {
    getHealth: () => api.get('/health'),
    getStatus: () => api.get('/'),
    getCircuitBreakers: () => api.get('/api/v2/circuit-breakers'),
    // Traces are under infrastructure router
    getTraces: (limit: number = 100) => api.get('/api/v2/infrastructure/traces', { params: { limit } }),
    getSystemHealth: () => api.get('/api/v2/infrastructure/health/system'),
  },
};

export { api };
export default api;
